/**
 * The naming contract — `{UNIT}_{LEVEL}_{DISCIPLINE}`.
 *
 * This module is the single place the convention is defined. Every layer
 * toggle, level isolation and picking rule parses these names, and nothing
 * else identifies a node. It has **no dependencies** — not even zod — so the
 * engine can import it without pulling a validation library into the bundle.
 *
 * Extending the convention (a basement level, a fifth discipline) is a
 * one-line change here and nowhere else. That is deliberate.
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** Disciplines, lower-case. This is the form the engine API speaks. */
export const DISCIPLINES = ['arch', 'struct', 'mech', 'elec'] as const;
export type Discipline = (typeof DISCIPLINES)[number];

/** Upper-case token as it appears inside a node or file name. */
export const DISCIPLINE_TOKEN: Readonly<Record<Discipline, string>> = {
  arch: 'ARCH',
  struct: 'STRUCT',
  mech: 'MECH',
  elec: 'ELEC',
};

const TOKEN_TO_DISCIPLINE: ReadonlyMap<string, Discipline> = new Map(
  DISCIPLINES.map((d) => [DISCIPLINE_TOKEN[d], d]),
);

/**
 * Non-unit nodes. `MASTER_TERRAIN`, `MASTER_CONTEXT`, plus the site furniture
 * split out of the terrain so it can cast shadows (the terrain itself is
 * receive-only by rule): `MASTER_BOUNDARY` is the plot walls and gates,
 * `MASTER_FLORA` the street planting.
 */
export const MASTER_PARTS = ['terrain', 'context', 'boundary', 'flora'] as const;
export type MasterPart = (typeof MASTER_PARTS)[number];

export const MASTER_PREFIX = 'MASTER';

/** Lighting variants a baked asset may ship in (§5A). */
export const LIGHTING_VARIANTS = ['day', 'night'] as const;
export type LightingVariant = (typeof LIGHTING_VARIANTS)[number];

// ---------------------------------------------------------------------------
// Patterns
// ---------------------------------------------------------------------------

/** `VL01`, `TH01`. Two-or-more upper-case alphanumerics, never `MASTER`. */
export const UNIT_PATTERN = /^[A-Z][A-Z0-9]{1,7}$/;

/** `L00`, `L01`, `L02`, `APT`. Extend here and nowhere else. */
export const LEVEL_PATTERN = /^(?:L\d{2}|APT)$/;

/** Blender appends `.001` to duplicated objects; glTF preserves it verbatim. */
const DUPLICATE_SUFFIX = /\.\d+$/;

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

export interface UnitNodeName {
  readonly kind: 'unit';
  readonly unit: string;
  readonly level: string;
  readonly discipline: Discipline;
  /** Canonical `{UNIT}_{LEVEL}_{DISCIPLINE}`, duplicate suffix stripped. */
  readonly canonical: string;
  readonly raw: string;
}

export interface MasterNodeName {
  readonly kind: 'master';
  readonly part: MasterPart;
  readonly canonical: string;
  readonly raw: string;
}

export type ParsedNodeName = UnitNodeName | MasterNodeName;

export function isDiscipline(value: string): value is Discipline {
  return (DISCIPLINES as readonly string[]).includes(value);
}

export function isMasterPart(value: string): value is MasterPart {
  return (MASTER_PARTS as readonly string[]).includes(value);
}

export function isLightingVariant(value: string): value is LightingVariant {
  return (LIGHTING_VARIANTS as readonly string[]).includes(value);
}

/**
 * Parse a scene-graph node name. Returns `null` for anything that is not part
 * of the contract — callers treat that as "not addressable", never as an error,
 * because glTF files legitimately carry helper nodes (armatures, empties).
 *
 * Tolerates lower-case input and Blender's `.001` duplicate suffix; everything
 * else must match exactly.
 */
export function parseNodeName(raw: string): ParsedNodeName | null {
  const stripped = raw.replace(DUPLICATE_SUFFIX, '');
  const upper = stripped.toUpperCase();
  const parts = upper.split('_');

  if (parts.length === 2 && parts[0] === MASTER_PREFIX) {
    const part = (parts[1] ?? '').toLowerCase();
    if (!isMasterPart(part)) return null;
    return { kind: 'master', part, canonical: upper, raw };
  }

  if (parts.length !== 3) return null;
  const [unit, level, disciplineToken] = parts as [string, string, string];
  if (unit === MASTER_PREFIX) return null;
  if (!UNIT_PATTERN.test(unit)) return null;
  if (!LEVEL_PATTERN.test(level)) return null;

  const discipline = TOKEN_TO_DISCIPLINE.get(disciplineToken);
  if (discipline === undefined) return null;

  return { kind: 'unit', unit, level, discipline, canonical: upper, raw };
}

/** Build a canonical node name. Throws on invalid input — this is a contract. */
export function formatNodeName(
  unit: string,
  level: string,
  discipline: Discipline,
): string {
  if (!UNIT_PATTERN.test(unit)) {
    throw new Error(`Invalid unit id "${unit}" — expected ${UNIT_PATTERN}`);
  }
  if (!LEVEL_PATTERN.test(level)) {
    throw new Error(`Invalid level code "${level}" — expected ${LEVEL_PATTERN}`);
  }
  return `${unit}_${level}_${DISCIPLINE_TOKEN[discipline]}`;
}

export function formatMasterName(part: MasterPart): string {
  return `${MASTER_PREFIX}_${part.toUpperCase()}`;
}

// ---------------------------------------------------------------------------
// Asset file names
// ---------------------------------------------------------------------------

export interface ParsedAssetFileName {
  readonly node: ParsedNodeName;
  readonly variant: LightingVariant | null;
  readonly file: string;
}

/**
 * Parse `VL01_L00_ARCH.glb` or `VL01_L00_ARCH_night.glb`.
 *
 * The lighting variant lives on the *file*, never on the node: two variants of
 * the same asset carry identical node names so `SceneRegistry` is unaffected by
 * which one happens to be resident.
 */
export function parseAssetFileName(file: string): ParsedAssetFileName | null {
  const base = file.replace(/^.*[/\\]/, '');
  const match = /^(.+)\.glb$/i.exec(base);
  if (!match) return null;
  const stem = match[1] as string;

  const variantMatch = /^(.+)_(day|night)$/i.exec(stem);
  if (variantMatch) {
    const node = parseNodeName(variantMatch[1] as string);
    const variantToken = (variantMatch[2] as string).toLowerCase();
    if (node && isLightingVariant(variantToken)) {
      return { node, variant: variantToken, file: base };
    }
  }

  const node = parseNodeName(stem);
  return node ? { node, variant: null, file: base } : null;
}

export function formatAssetFileName(
  canonicalNodeName: string,
  variant?: LightingVariant | null,
): string {
  return variant
    ? `${canonicalNodeName}_${variant}.glb`
    : `${canonicalNodeName}.glb`;
}

/**
 * Rewrite the unit token of a canonical node name.
 *
 * Repeated product types ship one model per *type*, instanced per unit (§10).
 * When the streaming layer instantiates `type_villa_apt` for `VL07` it clones
 * the representative unit's nodes and renames them through here, so downstream
 * every node still answers to the unit the buyer is actually looking at.
 */
export function rebaseNodeName(canonical: string, toUnit: string): string {
  const parsed = parseNodeName(canonical);
  if (!parsed) return canonical;
  if (parsed.kind === 'master') return parsed.canonical;
  return formatNodeName(toUnit, parsed.level, parsed.discipline);
}
